import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {ItemParams} from 'react-contexify';
import {contextMenu} from 'react-contexify';

import classNames from 'classnames';
import {reaction, toJS} from 'mobx';

import type {CoCoachFields} from '@yourcoach/shared/utils/entity/program/co-coaching';

import {useI18n} from '@yourcoach/i18n';
import {currentUserStore} from '@yourcoach/shared/api/user';
import CloneProgramIcon from '@yourcoach/shared/assets/icons/clone-program.svg';
import UserGroupPlusIcon from '@yourcoach/shared/assets/icons/user-group-plus.svg';
import {
  FULL_PORTION,
  parseDuration,
  parsePaymentPlan,
} from '@yourcoach/shared/utils/entity/program';
import {
  distributePortions,
  fillPortionsToFull,
} from '@yourcoach/shared/utils/entity/program/co-coaching';
import {logger} from '@yourcoach/shared/utils/logger';
import {getPriceString} from '@yourcoach/shared/utils/money';

import Button from '@src/components/Button';
import useIsVisible from '@src/hooks/useIsVisible';
import SelectCoachModal from '@src/modules/Coaches/SelectCoachModal';
import {ContextMenu} from '@src/v2/components/ContextMenu';

import type {ProgramT} from '.';

import CocoachingManagerListItem from './CocoachingManagerListItem';

import styles from './CocoachingManager.module.css';

const COACH_CONTEXT_MENU_ID = 'coach_context_menu';

export type Coach = ProgramT['expanded_coaches'][0] & CoCoachFields;

export type Ref = {
  getData: () => {
    coaches: ProgramT['coaches'];
    expandedCoaches: ProgramT['expanded_coaches'];
    isntProviderCoaches: ProgramT['expanded_coaches'];
  };
  removeIsntProviderCoaches: () => void;
};

type Props = {
  className?: string;
  cloneProgram: () => void;
  program: ProgramT;
  shouldCloneProgram: boolean;
  userIsOwner: boolean;
};

const CocoachingManager = forwardRef<Ref, Props>(
  (
    {className, cloneProgram, program, shouldCloneProgram, userIsOwner},
    ref,
  ) => {
    const {t} = useI18n();

    const [coaches, setCoaches] = useState<Coach[]>([]);

    const [addCoachModalIsOpen, showAddCoachModal, hideAddCoachModal] =
      useIsVisible();

    const lastEdition = useMemo(
      () => program.editions[program.editions.length - 1],
      [program.editions],
    );

    const parsedDuration = useMemo(
      () => parseDuration(lastEdition!.duration),
      [lastEdition],
    );

    const parsedPaymentPlan = useMemo(
      () => parsePaymentPlan(lastEdition!.payment_plan),
      [lastEdition],
    );

    const isEqualPortions = useMemo(() => {
      if (!parsedPaymentPlan.total) {
        return true;
      }

      return coaches.every(
        coach => coach.portion === FULL_PORTION / coaches.length,
      );
    }, [coaches, parsedPaymentPlan.total]);

    const ownerId = useMemo(
      () => program.user_id || currentUserStore.user!._id,
      [program.user_id],
    );

    const isReady = useRef(false);

    useEffect(() => {
      const defaultCoach = toJS(currentUserStore.user!);

      setCoaches(() => {
        setTimeout(() => {
          isReady.current = true;
        }, 5 * 16);

        return (
          JSON.parse(
            JSON.stringify(program.expanded_coaches || [defaultCoach]),
          ) as ProgramT['expanded_coaches']
        )
          .map(expandedCoach => {
            const coach = {...expandedCoach} as Coach;

            const {parsedCurrency, total} = parsedPaymentPlan;

            const {portion} = (program.coaches || {
              [defaultCoach._id]: {
                portion: FULL_PORTION,
              },
            })[coach._id!] || {
              portion: 0,
            };

            const price = Math.round(total * (portion / FULL_PORTION));

            coach.is_owner = coach._id === ownerId;
            coach.portion = portion;
            coach.price = getPriceString({
              currency: parsedCurrency,
              intZeroPrice: true,
              price,
            });

            return coach;
          })
          .sort((a, b) => {
            return a.is_owner === b.is_owner ? 0 : a.is_owner ? -1 : 1;
          });
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(
      () =>
        reaction(
          () => currentUserStore.user,
          user => {
            /**
             * Update the coaches list if current user data is changed
             * eg. when a current user connected Stripe
             */
            setCoaches(prev => {
              const index = prev.findIndex(coach => coach._id === user?._id);

              if (index >= 0) {
                const newCoaches = [...prev];

                newCoaches.splice(index, 1, {...prev[index]!, ...toJS(user)});

                return newCoaches;
              }

              return prev;
            });
          },
          {fireImmediately: true},
        ),
      [],
    );

    const resetPortions = useCallback(() => {
      const {parsedCurrency, total} = parsedPaymentPlan;

      const portion = FULL_PORTION / coaches.length;
      const price = getPriceString({
        currency: parsedCurrency,
        intZeroPrice: true,
        price: Math.round(total * (portion / FULL_PORTION)),
      });

      setCoaches(prev =>
        prev.map(coach => ({
          ...coach,
          portion,
          price,
        })),
      );
    }, [coaches.length, parsedPaymentPlan]);

    useEffect(() => {
      if (isReady.current && coaches.length && !shouldCloneProgram) {
        resetPortions();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coaches.length]);

    const onAddCoachButtonClick = useCallback(() => {
      showAddCoachModal();

      logger.event('add_coach_tap');
    }, [showAddCoachModal]);

    const onAddCoaches = useCallback(
      (addedCoaches: Coach[]) => {
        if (addedCoaches.length) {
          logger.event('add_coach_success');
        }

        hideAddCoachModal();
        setCoaches(prev => {
          const newCoaches = [...prev];

          addedCoaches.forEach(addedCoach => {
            if (!newCoaches.find(coach => coach._id === addedCoach._id)) {
              newCoaches.push(addedCoach);
            }
          });

          return newCoaches;
        });
      },
      [hideAddCoachModal],
    );

    const deleteCoach = useCallback((coach: Coach) => {
      setCoaches(prev => {
        const index = prev.findIndex(item => item._id === coach._id);

        if (index >= 0) {
          prev.splice(index, 1);
        }

        return [...prev];
      });
    }, []);

    const onDeleteCoachButtonClick = useCallback(
      ({props}: ItemParams<{coach: Coach}>) => {
        if (props) {
          const {coach} = props;

          deleteCoach(coach);
        }
      },
      [deleteCoach],
    );

    const onCoachMoreButtonClick = useCallback(
      (coach: Coach, event: React.MouseEvent<Element, MouseEvent>) => {
        contextMenu.show({
          event,
          id: COACH_CONTEXT_MENU_ID,
          props: {
            coach,
          },
        });
      },
      [],
    );

    const renderContextMenu = useCallback(
      () => (
        <ContextMenu.Menu animation="fade" id={COACH_CONTEXT_MENU_ID}>
          <ContextMenu.Item onClick={onDeleteCoachButtonClick} variant="error">
            {t('shared:delete')}
          </ContextMenu.Item>
        </ContextMenu.Menu>
      ),
      [onDeleteCoachButtonClick, t],
    );

    const getData = useCallback(() => {
      const coachesData: ProgramT['coaches'] = {};

      const expandedCoaches = coaches.map(coach => {
        const copy = {...coach};

        // cleanup
        // @ts-expect-error: we can delete these fields. it's JS baby
        delete copy.is_owner;
        // @ts-expect-error: we can delete these fields. it's JS baby
        delete copy.portion;
        // @ts-expect-error: we can delete these fields. it's JS baby
        delete copy.price;

        return copy;
      });

      const isntProviderCoaches: Coach[] = [];

      const portions = fillPortionsToFull(
        coaches.map(coach => ({...coach, portion: Math.round(coach.portion)})),
      ).map(coach => coach.portion);

      coaches.forEach((coach, i) => {
        if (coach._id) {
          coachesData[coach._id] = {
            ...(program.coaches || {})[coach._id],
            portion: portions[i],
            role: 'Coach',
          };

          if (
            parsedPaymentPlan.total &&
            parsedPaymentPlan.processing === 'internal' &&
            !coach.is_provider
          ) {
            isntProviderCoaches.push(coach);
          }
        }
      });

      return {
        coaches: coachesData,
        expandedCoaches,
        isntProviderCoaches,
      };
    }, [
      coaches,
      parsedPaymentPlan.processing,
      parsedPaymentPlan.total,
      program,
    ]);

    const removeIsntProviderCoaches = useCallback(() => {
      const {isntProviderCoaches} = getData();

      isntProviderCoaches.forEach(coach => {
        deleteCoach(coach);
      });

      if (isntProviderCoaches.length) {
        resetPortions();
      }
    }, [deleteCoach, getData, resetPortions]);

    useImperativeHandle(ref, () => ({
      getData,
      removeIsntProviderCoaches,
    }));

    const onCoachPriceChange = useCallback(
      (text: string, coach: Coach) => {
        const {parsedCurrency, total} = parsedPaymentPlan;

        const rawPrice = parseFloat(
          text
            .replace(/[^\d., d]/g, '')
            .replace(parsedCurrency.sign, '')
            .replace(',', '.'),
        );

        const price = ((rawPrice || 0) * 1000) / 10;

        let priceText = text.replace(parsedCurrency.sign, '');

        if (priceText === '.' || priceText === ',') {
          priceText = `${parsedCurrency.sign}0.`;
        } else if (priceText === '0' || priceText === '') {
          priceText = `${parsedCurrency.sign}0`;
        } else if (priceText.includes('.') || priceText.includes(',')) {
          let point = '.';

          if (priceText.includes(',')) {
            point = ',';
          }

          const tmp = priceText.split(point);

          priceText = `${parsedCurrency.sign}${tmp[0]}${point}${tmp[1]!.slice(
            0,
            2,
          )}`;
        } else {
          priceText = `${parsedCurrency.sign}${rawPrice}`;
        }

        // current coach portion
        const portion =
          price <= total ? (price / total) * FULL_PORTION : FULL_PORTION;

        setCoaches(prev =>
          distributePortions<Coach>({
            coach,
            coaches: prev,
            parsedPaymentPlan,
            portion,
            price: priceText,
          }),
        );
      },
      [parsedPaymentPlan],
    );

    return (
      <div className={classNames(styles.container, className)}>
        <div className={styles.titleContainer}>
          <div className="flex1">
            <h3 className={styles.title}>
              {t('shared:CocoachingManager.cocoaching_label')}
            </h3>
            {!shouldCloneProgram && userIsOwner ? (
              <p className={styles.message}>
                {t('shared:CocoachingManager.description_label')}
              </p>
            ) : null}
          </div>
          <div className={styles.titleButtonsContainer}>
            {!isEqualPortions && userIsOwner && !shouldCloneProgram ? (
              <Button className={styles.resetButton} onClick={resetPortions}>
                {t('shared:CocoachingManager.reset_button')}
              </Button>
            ) : null}
            {!shouldCloneProgram && userIsOwner ? (
              <>
                <Button
                  className={styles.addButton}
                  onClick={onAddCoachButtonClick}
                >
                  <UserGroupPlusIcon />
                  {t('shared:CocoachingManager.add_coach_button')}
                </Button>
                <SelectCoachModal
                  canAcceptPayments={
                    !!(
                      parsedPaymentPlan.total &&
                      parsedPaymentPlan.processing === 'internal'
                    )
                  }
                  isOpen={addCoachModalIsOpen}
                  onAfterClose={hideAddCoachModal}
                  // @ts-expect-error: TODO: fix this
                  onSelect={onAddCoaches}
                  title={t('shared:CocoachingManager.add_coach_modal_title')}
                />
              </>
            ) : null}
          </div>
        </div>
        <div className={styles.list}>
          {coaches.map(coach => {
            const {processing, total} = parsedPaymentPlan;

            let ableToDelete = userIsOwner && !shouldCloneProgram;

            if (
              ownerId === coach._id ||
              currentUserStore.user!._id === coach._id
            ) {
              ableToDelete = false;
            }

            let role: 'cocoach' | 'owner' | 'you';
            let status: 'approved' | 'pending' | 'rejected' | undefined;

            if (coach.is_owner) {
              role = userIsOwner ? 'you' : 'owner';
            } else if (currentUserStore.user!._id === coach._id) {
              role = 'you';
            } else {
              role = 'cocoach';
            }

            if (
              coach._id &&
              !coach.is_owner &&
              program.coaches &&
              program.coaches[coach._id] &&
              program.status === 'draft'
            ) {
              if (program.coaches[coach._id].signed) {
                status = 'approved';
              } else if (program.coaches[coach._id].resigned) {
                status = 'rejected';
              } else {
                status = 'pending';
              }
            }

            const cantAcceptPayments = !!(
              total &&
              processing === 'internal' &&
              !coach.is_provider
            );

            return (
              <CocoachingManagerListItem
                cantAcceptPayments={cantAcceptPayments}
                coach={coach}
                key={coach._id}
                onMoreButtonClick={
                  ableToDelete ? onCoachMoreButtonClick : undefined
                }
                onPriceChange={
                  total && coaches.length > 1 ? onCoachPriceChange : undefined
                }
                parsedDuration={parsedDuration}
                parsedPaymentPlan={parsedPaymentPlan}
                priceIsEditable={
                  userIsOwner && !shouldCloneProgram && !cantAcceptPayments
                }
                role={role}
                showMoreButton={userIsOwner && !shouldCloneProgram}
                status={status}
              />
            );
          })}
        </div>
        {shouldCloneProgram && userIsOwner ? (
          <div className={styles.cloneContainer}>
            <div className={styles.message}>
              {t('shared:CocoachingManager.edit_label')}
            </div>
            <Button className={styles.cloneButton} onClick={cloneProgram}>
              <CloneProgramIcon />
              {t('shared:CocoachingManager.clone_program_button')}
            </Button>
          </div>
        ) : null}
        {renderContextMenu()}
      </div>
    );
  },
);

export default CocoachingManager;
